import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import {
  Users,
  Mail,
  Calendar,
  CheckCircle,
  XCircle,
  Clock,
  Send,
  RefreshCw,
  User,
  Search,
  Filter
} from 'lucide-react';
import { AdminLayout, AdminStatCard } from '../../components/admin/layout';

function PromoCodeRequests() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0
  });
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [processingId, setProcessingId] = useState(null);

  useEffect(() => {
    checkAdminAccess();
  }, []);

  const checkAdminAccess = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        navigate('/auth/login');
        return;
      }

      const { data: profile } = await supabase
        .from('profiles')
        .select('role')
        .eq('id', user.id)
        .single();

      if (profile?.role === 'admin') {
        setIsAdmin(true);
        await loadRequests();
      } else {
        navigate('/');
      }
    } catch (error) {
      console.error('Error checking admin access:', error);
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const loadRequests = async () => {
    try {
      setRefreshing(true);
      
      // 프로모션 코드 요청 목록 로드
      const { data, error } = await supabase
        .from('promo_code_requests')
        .select(`
          *,
          profiles (
            id,
            email,
            name,
            avatar_url
          )
        `)
        .order('requested_at', { ascending: false });

      if (error) throw error;

      setRequests(data || []);
      
      // 통계 계산
      const total = data?.length || 0;
      const pending = data?.filter(r => r.status === 'pending').length || 0;
      const approved = data?.filter(r => r.status === 'approved').length || 0;
      const rejected = data?.filter(r => r.status === 'rejected').length || 0;
      
      setStats({ total, pending, approved, rejected });
    } catch (error) {
      console.error('Error loading requests:', error);
      alert('요청 목록을 불러오는 중 오류가 발생했습니다.');
    } finally {
      setRefreshing(false);
    }
  };

  const approveRequest = async (requestId, userId, email, userName) => {
    if (!window.confirm('이 요청을 승인하시겠습니까? 프로모션 코드가 생성되고 이메일로 발송됩니다.')) {
      return;
    }

    setProcessingId(requestId);

    try {
      // 프로모션 코드 생성
      const promoCode = generatePromoCode();
      const expiryDate = new Date();
      expiryDate.setDate(expiryDate.getDate() + 30); // 30일 후 만료

      // 현재 사용자 정보 가져오기
      const { data: { user } } = await supabase.auth.getUser();

      // 프로모션 코드 저장
      const { data: promoData, error: promoError } = await supabase
        .from('promo_codes')
        .insert({
          code: promoCode,
          description: `${userName || '사용자'}의 요청으로 생성된 프로모션 코드`,
          type: 'promotion',
          max_uses: 1,
          max_uses_per_user: 1,
          valid_until: expiryDate.toISOString(),
          benefit_duration_days: 90,
          created_by: user.id
        })
        .select()
        .single();

      if (promoError) throw promoError;

      // 요청 상태 업데이트
      const { error: updateError } = await supabase
        .from('promo_code_requests')
        .update({
          status: 'approved',
          approved_at: new Date().toISOString(),
          promo_code_id: promoData.id
        })
        .eq('id', requestId);

      if (updateError) throw updateError;

      // 이메일 발송 API 호출
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const response = await fetch(`${process.env.REACT_APP_SERVER_URL || ''}/api/promo-code/approve`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token}`
          },
          body: JSON.stringify({
            requestId,
            userId,
            email,
            userName,
            promoCode
          })
        });

        const result = await response.json();
        
        if (!response.ok) {
          console.error('이메일 발송 API 오류:', result);
          alert(`프로모션 코드가 성공적으로 생성되었습니다!\n코드: ${promoCode}\n\n이메일 발송에 실패했습니다. 사용자에게 직접 코드를 전달해주세요.`);
        } else if (result.emailSent) {
          alert(`프로모션 코드가 성공적으로 생성되었습니다!\n코드: ${promoCode}\n\n이메일이 ${email}로 발송되었습니다.`);
        } else {
          alert(`프로모션 코드가 성공적으로 생성되었습니다!\n코드: ${promoCode}\n\n${result.message || '이메일 발송에 실패했습니다.'}`);
        }
      } catch (emailError) {
        console.error('이메일 발송 중 오류:', emailError);
        alert(`프로모션 코드가 성공적으로 생성되었습니다!\n코드: ${promoCode}\n\n이메일 발송에 실패했습니다. 사용자에게 직접 코드를 전달해주세요.`);
      }
      
      await loadRequests();
    } catch (error) {
      console.error('Error approving request:', error);
      alert(`요청 승인 중 오류가 발생했습니다: ${error.message}`);
    } finally {
      setProcessingId(null);
    }
  };

  // 프로모션 코드 생성 함수
  const generatePromoCode = () => {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = 'PROMOTION-';
    for (let i = 0; i < 8; i++) {
      code += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    return code;
  };

  // 이메일 재발송 함수
  const resendEmail = async (request) => {
    if (!window.confirm(`${request.email}로 프로모션 코드를 재발송하시겠습니까?`)) {
      return;
    }

    setProcessingId(request.id);

    try {
      // 프로모션 코드 정보 가져오기
      const { data: promoCode, error: fetchError } = await supabase
        .from('promo_codes')
        .select('code')
        .eq('id', request.promo_code_id)
        .single();

      if (fetchError || !promoCode) {
        throw new Error('프로모션 코드를 찾을 수 없습니다.');
      }

      // 이메일 발송 API 호출
      const { data: { session } } = await supabase.auth.getSession();
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL || ''}/api/promo-code/send-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`
        },
        body: JSON.stringify({
          email: request.email,
          userName: request.profiles?.name,
          promoCode: promoCode.code
        })
      });

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.error || '이메일 발송에 실패했습니다.');
      }

      alert(`이메일이 ${request.email}로 성공적으로 재발송되었습니다.`);
    } catch (error) {
      console.error('이메일 재발송 중 오류:', error);
      alert(`이메일 재발송 중 오류가 발생했습니다: ${error.message}`);
    } finally {
      setProcessingId(null);
    }
  };

  const rejectRequest = async (requestId) => {
    if (!window.confirm('이 요청을 거절하시겠습니까?')) {
      return;
    }

    setProcessingId(requestId);

    try {
      const { error } = await supabase
        .from('promo_code_requests')
        .update({
          status: 'rejected',
          rejected_at: new Date().toISOString()
        })
        .eq('id', requestId);

      if (error) throw error;
      
      await loadRequests();
    } catch (error) {
      console.error('Error rejecting request:', error);
      alert('요청 거절 중 오류가 발생했습니다.');
    } finally {
      setProcessingId(null);
    }
  };

  const getFilteredRequests = () => {
    let filtered = requests;
    
    if (filter !== 'all') {
      filtered = filtered.filter(r => r.status === filter);
    }
    
    if (searchTerm) {
      filtered = filtered.filter(r => 
        r.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        r.profiles?.name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    return filtered;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const actionButtons = (
    <>
      <button
        onClick={loadRequests}
        disabled={refreshing}
        className="flex items-center gap-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 disabled:bg-gray-400"
      >
        <RefreshCw className={`h-5 w-5 ${refreshing ? 'animate-spin' : ''}`} />
        새로고침
      </button>
    </>
  );

  return (
    <AdminLayout
      title="프로모션 코드 요청 관리"
      description="사용자의 프로모션 코드 요청을 검토하고 승인합니다."
      icon={<Mail className="h-8 w-8 text-blue-600" />}
      actions={actionButtons}
      isAdmin={isAdmin}
      loading={loading}
    >
      {/* 통계 카드 */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <AdminStatCard
          icon={<Users />}
          title="전체 요청"
          value={stats.total}
          iconColorClass="text-blue-600"
        />
        
        <AdminStatCard
          icon={<Clock />}
          title="대기 중"
          value={stats.pending}
          iconColorClass="text-yellow-600"
        />
        
        <AdminStatCard
          icon={<CheckCircle />}
          title="승인됨"
          value={stats.approved}
          iconColorClass="text-green-600"
        />
        
        <AdminStatCard
          icon={<XCircle />}
          title="거절됨"
          value={stats.rejected}
          iconColorClass="text-red-600"
        />
      </div>

      {/* 필터 및 검색 */}
      <div className="bg-white shadow-lg rounded-lg p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex items-center gap-2 flex-1">
            <Filter className="h-5 w-5 text-gray-400" />
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">전체</option>
              <option value="pending">대기 중</option>
              <option value="approved">승인됨</option>
              <option value="rejected">거절됨</option>
            </select>
          </div>
          
          <div className="flex items-center gap-2 flex-1">
            <Search className="h-5 w-5 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="이름 또는 이메일로 검색"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {/* 요청 목록 */}
      <div className="bg-white shadow-lg rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  요청자
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  요청일시
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  상태
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  작업
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {getFilteredRequests().map((request) => (
                <tr key={request.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        {request.profiles?.avatar_url ? (
                          <img
                            src={request.profiles.avatar_url}
                            alt=""
                            className="h-10 w-10 rounded-full"
                          />
                        ) : (
                          <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                            <User className="h-5 w-5 text-gray-500" />
                          </div>
                        )}
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {request.profiles?.name || '이름 없음'}
                        </div>
                        <div className="text-sm text-gray-500">
                          {request.email}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {formatDate(request.requested_at)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      request.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      request.status === 'approved' ? 'bg-green-100 text-green-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {request.status === 'pending' ? '대기 중' :
                       request.status === 'approved' ? '승인됨' : '거절됨'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    {request.status === 'pending' && (
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => approveRequest(request.id, request.user_id, request.email, request.profiles?.name)}
                          disabled={processingId === request.id}
                          className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:bg-gray-400"
                        >
                          <CheckCircle className="h-3.5 w-3.5 mr-1" />
                          승인
                        </button>
                        <button
                          onClick={() => rejectRequest(request.id)}
                          disabled={processingId === request.id}
                          className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:bg-gray-400"
                        >
                          <XCircle className="h-3.5 w-3.5 mr-1" />
                          거절
                        </button>
                      </div>
                    )}
                    {request.status === 'approved' && (
                      <div className="flex flex-col items-center gap-1">
                        <span className="text-sm text-gray-500">
                          {request.approved_at && formatDate(request.approved_at)}
                        </span>
                        <button
                          onClick={() => resendEmail(request)}
                          disabled={processingId === request.id}
                          className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded-md text-blue-600 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-100"
                        >
                          <Send className="h-3 w-3 mr-1" />
                          이메일 재발송
                        </button>
                      </div>
                    )}
                    {request.status === 'rejected' && (
                      <span className="text-sm text-gray-500">
                        {request.rejected_at && formatDate(request.rejected_at)}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {getFilteredRequests().length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500">조건에 맞는 요청이 없습니다.</p>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}

export default PromoCodeRequests;
